/*
 * CaroFit entitlement-bridge — production theme script (Path B).
 *
 * Reads the store token minted by carofit.app (?cf_token=... or #cf_token=...),
 * persists it to localStorage, attaches it to the Shopify cart as the
 * `carofit_token` attribute (POST /cart/update.js + a hidden cart-form field),
 * cleans the token from the visible URL, and re-applies the stored token on
 * every navigation so it survives until checkout.
 *
 * The orders/create webhook performs the authoritative HS256 + audience
 * verification; this script only does a shape check before sending.
 *
 * Behavioural logic is mirrored 1:1 in `carofit-bridge.core.mjs`, which is
 * exercised by tests/carofit-bridge.test.mjs and tests/carofit-bridge.production.test.mjs.
 */
(function () {
  'use strict';

  var CAROFIT_TOKEN_KEY = 'carofit_token';
  var CAROFIT_GA_CID_KEY = 'carofit_ga_cid';
  var URL_PARAM_KEY = 'cf_token';
  var STORAGE_KEY = 'carofit_store_token';
  var CART_FORM_FIELD_NAME = 'attributes[' + CAROFIT_TOKEN_KEY + ']';
  var GA_CID_FORM_FIELD_NAME = 'attributes[' + CAROFIT_GA_CID_KEY + ']';
  var CART_FORM_SELECTOR = 'form[action$="/cart"], form[action*="/cart/add"]';
  var MAX_TOKEN_LENGTH = 4096;
  var JWT_SEGMENT_COUNT = 3;
  var JWT_SEGMENT = /^[A-Za-z0-9_-]+$/;

  function decodeToken(raw) {
    try {
      return decodeURIComponent(raw);
    } catch (e) {
      return null;
    }
  }

  function readParam(source) {
    if (!source) return null;
    var cleaned = source.replace(/^[?#]/, '');
    if (!cleaned) return null;
    var params = new URLSearchParams(cleaned);
    var value = params.get(URL_PARAM_KEY);
    return value ? decodeToken(value) : null;
  }

  function extractTokenFromLocation(location) {
    return readParam(location.search) || readParam(location.hash) || null;
  }

  function looksLikeStoreToken(token) {
    if (typeof token !== 'string') return false;
    if (token.length === 0 || token.length > MAX_TOKEN_LENGTH) return false;
    var segments = token.split('.');
    if (segments.length !== JWT_SEGMENT_COUNT) return false;
    return segments.every(function (seg) {
      return seg.length > 0 && JWT_SEGMENT.test(seg);
    });
  }

  function buildCleanUrl(location) {
    var url = new URL(location.pathname + (location.search || ''), location.origin);
    url.searchParams.delete(URL_PARAM_KEY);
    var hash = location.hash || '';
    if (hash) {
      var params = new URLSearchParams(hash.replace(/^#/, ''));
      params.delete(URL_PARAM_KEY);
      var rest = params.toString();
      hash = rest ? '#' + rest : '';
    }
    return url.toString() + hash;
  }

  function parseGaClientId(cookie) {
    if (!cookie) return null;
    var match = cookie.match(/(?:^|;\s*)_ga=([^;]+)/);
    if (!match) return null;
    var parts = match[1].split('.');
    if (parts.length < 4) return null;
    return parts[parts.length - 2] + '.' + parts[parts.length - 1];
  }

  function buildCartAttributesBody(attrs) {
    attrs = attrs || {};
    var attributes = {};
    if (attrs.token) attributes[CAROFIT_TOKEN_KEY] = attrs.token;
    if (attrs.gaCid) attributes[CAROFIT_GA_CID_KEY] = attrs.gaCid;
    return JSON.stringify({ attributes: attributes });
  }

  function buildCartUpdateBody(token) {
    return buildCartAttributesBody({ token: token });
  }

  function createBridge(deps) {
    function readStored() {
      try {
        var value = deps.storage.getItem(STORAGE_KEY);
        return looksLikeStoreToken(value) ? value : null;
      } catch (e) {
        return null;
      }
    }

    function persist(token) {
      try {
        deps.storage.setItem(STORAGE_KEY, token);
      } catch (e) {
        /* storage unavailable (private mode) — token still applied this page */
      }
    }

    function writeHiddenField(fieldName, value) {
      var forms;
      try {
        forms = deps.document.querySelectorAll(CART_FORM_SELECTOR);
      } catch (e) {
        return;
      }
      Array.prototype.forEach.call(forms, function (form) {
        var existing = form.querySelector('input[name="' + fieldName + '"]');
        if (existing) {
          existing.value = value;
          return;
        }
        var input = deps.document.createElement('input');
        input.type = 'hidden';
        input.name = fieldName;
        input.value = value;
        form.appendChild(input);
      });
    }

    function writeHiddenFields(attrs) {
      if (attrs.token) writeHiddenField(CART_FORM_FIELD_NAME, attrs.token);
      if (attrs.gaCid) writeHiddenField(GA_CID_FORM_FIELD_NAME, attrs.gaCid);
    }

    function postToCart(attrs) {
      if (!deps.cartUpdateUrl) return Promise.resolve();
      return deps
        .fetch(deps.cartUpdateUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: buildCartAttributesBody(attrs),
        })
        .catch(function () {
          /* transient network error — hidden field + next navigation retries */
        });
    }

    function run() {
      var fromUrl = extractTokenFromLocation(deps.location);
      var validUrlToken = looksLikeStoreToken(fromUrl) ? fromUrl : null;

      if (validUrlToken) {
        persist(validUrlToken);
      }
      if (fromUrl) {
        try {
          deps.replaceUrl(buildCleanUrl(deps.location));
        } catch (e) {
          /* history API unavailable — non-fatal */
        }
      }

      var token = validUrlToken || readStored();
      var gaCid = parseGaClientId(deps.cookie || '');
      if (!token && !gaCid) return Promise.resolve();

      var attrs = { token: token, gaCid: gaCid };
      writeHiddenFields(attrs);
      return postToCart(attrs);
    }

    function applyStoredToFields() {
      var token = readStored();
      var gaCid = parseGaClientId(deps.cookie || '');
      if (!token && !gaCid) return;
      writeHiddenFields({ token: token, gaCid: gaCid });
    }

    return { run: run, applyStoredToFields: applyStoredToFields };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      CAROFIT_TOKEN_KEY: CAROFIT_TOKEN_KEY,
      CAROFIT_GA_CID_KEY: CAROFIT_GA_CID_KEY,
      URL_PARAM_KEY: URL_PARAM_KEY,
      STORAGE_KEY: STORAGE_KEY,
      CART_FORM_FIELD_NAME: CART_FORM_FIELD_NAME,
      GA_CID_FORM_FIELD_NAME: GA_CID_FORM_FIELD_NAME,
      extractTokenFromLocation: extractTokenFromLocation,
      looksLikeStoreToken: looksLikeStoreToken,
      parseGaClientId: parseGaClientId,
      buildCleanUrl: buildCleanUrl,
      buildCartUpdateBody: buildCartUpdateBody,
      buildCartAttributesBody: buildCartAttributesBody,
      createBridge: createBridge,
    };
    return;
  }

  if (typeof window === 'undefined') return;

  var config = window.CarofitBridgeConfig || {};

  function defaultReplaceUrl(url) {
    if (window.history && window.history.replaceState) {
      window.history.replaceState(window.history.state, document.title, url);
    }
  }

  var safeFetch =
    typeof window.fetch === 'function'
      ? window.fetch.bind(window)
      : function () {
          return Promise.resolve({ ok: false });
        };

  function buildBridge() {
    return createBridge({
      location: window.location,
      storage: window.localStorage,
      fetch: safeFetch,
      replaceUrl: defaultReplaceUrl,
      cartUpdateUrl: config.cartUpdateUrl || '/cart/update.js',
      document: document,
      cookie: typeof document !== 'undefined' ? document.cookie : '',
    });
  }

  function start() {
    buildBridge().run();
  }

  function refreshHiddenFields() {
    buildBridge().applyStoredToFields();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', refreshHiddenFields);
})();
